import { z } from 'zod';
import { solarDesignSchema } from './design.js';

const strict = <T extends z.ZodRawShape>(shape: T) => z.object(shape).strict();
const pointMmSchema = strict({ x: z.number().finite(), y: z.number().finite() });
type Point = z.infer<typeof pointMmSchema>;

function cross(a: Point, b: Point, c: Point): number {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

function onSegment(a: Point, b: Point, p: Point): boolean {
  return cross(a, b, p) === 0 && p.x >= Math.min(a.x, b.x) && p.x <= Math.max(a.x, b.x)
    && p.y >= Math.min(a.y, b.y) && p.y <= Math.max(a.y, b.y);
}

function intersects(a: Point, b: Point, c: Point, d: Point): boolean {
  const abC = cross(a, b, c);
  const abD = cross(a, b, d);
  const cdA = cross(c, d, a);
  const cdB = cross(c, d, b);
  if (![abC, abD, cdA, cdB].every(Number.isFinite)) return true;
  return (abC === 0 && onSegment(a, b, c)) || (abD === 0 && onSegment(a, b, d))
    || (cdA === 0 && onSegment(c, d, a)) || (cdB === 0 && onSegment(c, d, b))
    || ((abC > 0) !== (abD > 0) && (cdA > 0) !== (cdB > 0));
}

/** Closed implicitly: the final point must not repeat the first. */
export const outlineMmSchema = z.array(pointMmSchema).min(3).max(128).superRefine((points, ctx) => {
  const seen = new Set<string>();
  for (const point of points) {
    const key = `${point.x},${point.y}`;
    if (seen.has(key)) {
      ctx.addIssue({ code: 'custom', message: 'Outline has a repeated vertex' });
      break;
    }
    seen.add(key);
  }
  let twiceArea = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % points.length]!;
    twiceArea += a.x * b.y - b.x * a.y;
  }
  if (!Number.isFinite(twiceArea) || twiceArea === 0) {
    ctx.addIssue({ code: 'custom', message: 'Outline must have nonzero finite area' });
  }
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      if (j === i + 1 || (i === 0 && j === points.length - 1)) continue;
      if (intersects(points[i]!, points[(i + 1) % points.length]!, points[j]!, points[(j + 1) % points.length]!)) {
        ctx.addIssue({ code: 'custom', message: 'Outline self-intersects' });
        return;
      }
    }
  }
});

const rectangleObstacleSchema = solarDesignSchema.shape.roofs.element.shape.obstacles.element;
const polygonObstacleSchema = strict({
  shape: z.literal('POLYGON'), id: z.string(), kind: z.string(),
  outlineMm: outlineMmSchema, clearanceMm: z.number().finite().nonnegative(),
});

const geoPointSchema = strict({ latitude: z.number().finite().min(-90).max(90), longitude: z.number().finite().min(-180).max(180) });
const geoReferenceSchema = strict({
  location: geoPointSchema, vertices: z.array(geoPointSchema), provider: z.string(), source: z.string(),
  imageryDate: z.string().optional(), scaleConfirmed: z.boolean(),
});

const roofV1Schema = solarDesignSchema.shape.roofs.element;
const roofV2Schema = roofV1Schema.extend({
  outlineMm: outlineMmSchema,
  geometrySource: z.enum(['MEASURED', 'MAP_TRACE']),
  obstacles: z.array(z.union([rectangleObstacleSchema, polygonObstacleSchema])),
  geoReference: geoReferenceSchema.optional(),
});

const arrayV1Schema = solarDesignSchema.shape.arrays.element;
const gridArraySchema = arrayV1Schema.extend({ placementMode: z.literal('GRID') });
const manualArraySchema = arrayV1Schema.omit({ orientation: true, rows: true, columns: true, originMm: true, skipped: true }).extend({
  placementMode: z.literal('MANUAL'),
  placements: z.array(strict({ id: z.string(), xMm: z.number().finite(), yMm: z.number().finite(), orientation: z.enum(['portrait', 'landscape']) })),
});

export const solarDesignV2Schema = solarDesignSchema.extend({
  schemaVersion: z.literal(2),
  roofs: z.array(roofV2Schema),
  arrays: z.array(z.discriminatedUnion('placementMode', [gridArraySchema, manualArraySchema])),
}).superRefine((design, ctx) => {
  let count = 0;
  const ids = new Set<string>();
  for (const [arrayIndex, array] of design.arrays.entries()) {
    if (array.placementMode !== 'MANUAL') continue;
    count += array.placements.length;
    for (const [placementIndex, placement] of array.placements.entries()) {
      if (ids.has(placement.id)) ctx.addIssue({ code: 'custom', path: ['arrays', arrayIndex, 'placements', placementIndex, 'id'], message: 'Placement ID must be unique across the design' });
      ids.add(placement.id);
    }
  }
  if (count > 2000) ctx.addIssue({ code: 'custom', path: ['arrays'], message: 'Design exceeds 2000 manual placements' });
});

export type SolarDesignV2 = z.infer<typeof solarDesignV2Schema>;
export const persistedSolarDesignSchema = z.union([solarDesignSchema, solarDesignV2Schema]);
export type PersistedSolarDesign = z.infer<typeof persistedSolarDesignSchema>;
